import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Card, CardContent, Button, Checkbox, FormControlLabel,
  Dialog, DialogTitle, DialogContent, IconButton, Grid, LinearProgress,
  List, ListItem, ListItemText, Alert
} from '@mui/material';
import {
  Close as CloseIcon, PlayArrow as PlayIcon, Description as DescriptionIcon
} from '@mui/icons-material';
import ReactPlayer from 'react-player';
import { hasReadPdf, hasWatchedVideo } from '../../hooks/TimeTracking';
import { useAuth } from '../../context/AuthContext';
import { API_URL } from '../../config/api';

const TrainingStep = ({ data, onChange }) => {
  const { user, getAuthHeaders } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [videoOpen, setVideoOpen] = useState(false);
  const [documentOpen, setDocumentOpen] = useState(false);
  const [currentDocument, setCurrentDocument] = useState(null);
  const [pdfStartTime, setPdfStartTime] = useState(null);
  const [watchedSeconds, setWatchedSeconds] = useState(0);
  const [lastPlayedSeconds, setLastPlayedSeconds] = useState(0);
  const [showEarlyCloseWarning, setShowEarlyCloseWarning] = useState(false);
  const [quizStarted, setQuizStarted] = useState(false);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  const [quizScore, setQuizScore] = useState(null);

  useEffect(() => {
    const fetchDocumentsAndProgress = async () => {
      try {
        // Fetch documents
        const docRes = await fetch(`${API_URL}/documents?module=Training`, {
          headers: getAuthHeaders()
        });
        const docData = await docRes.json();
        setDocuments(docData.documents);

        // Fetch progress
        const progressRes = await fetch(`${API_URL}/progress/user-progress?userId=${user.id}&module=Training`, {
          method: 'GET',
          headers: getAuthHeaders()
        });

        if (!progressRes.ok) {
          throw new Error('Failed to fetch user progress');
        }

        const progressData = await progressRes.json();
        console.log('Progress Data:', progressData);
        
        // Check if the API returned a valid progress object or a completion message
        if (progressData && typeof progressData === 'object' && !progressData.message) {
          // Valid progress data - update the state
          onChange(progressData);
        } else if (progressData && progressData.completed && progressData.message === "No trackable documents to complete") {
          // No trackable documents - don't update progress state, keep existing data
          console.log('No trackable documents found, keeping existing progress state');
        } else {
          // Invalid or unexpected response format - initialize with empty progress
          console.warn('Unexpected progress response format:', progressData);
          // Don't call onChange to avoid corrupting the state
        }
      } catch (error) {
        console.error('Error fetching documents or progress:', error);
      }
    };

    if (user) {
      fetchDocumentsAndProgress();
    }
  }, [user, getAuthHeaders]);

  const updateProgress = async (documentId) => {
    try {
      await fetch(`${API_URL}/progress/user-progress`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          userId: user.id,
          documentId,
          status: true
        })
      });
    } catch (error) {
      console.error('Failed to update progress:', error);
    }
  };

  const handleDocumentRead = async (documentId) => {
    onChange({ ...data, [documentId]: true });
    if (currentDocument?.isTrackable) {
      await updateProgress(documentId);
    }
  };

  const handleVideoComplete = async (documentId) => {
    onChange({ ...data, [documentId]: true });
    if (currentDocument?.isTrackable) {
      await updateProgress(documentId);
    }
  };

  const handleOpenDocument = (doc) => {
    setCurrentDocument(doc);
    setDocumentOpen(true);
    setPdfStartTime(Date.now());
  };

  const handleCloseDocument = async () => {
    const endTime = Date.now();
    const alreadyCompleted = data[currentDocument.documentId];
    const isTrackable = currentDocument.isTrackable;

    if (!isTrackable || alreadyCompleted || hasReadPdf(pdfStartTime, endTime, currentDocument.pageCount)) {
      if (!alreadyCompleted) {
        await handleDocumentRead(currentDocument.documentId);
      }
      setDocumentOpen(false);
    } else {
      setShowEarlyCloseWarning(true);
    }
  };

  const handleOpenVideo = (doc) => {
    setCurrentDocument(doc);
    setVideoOpen(true);
    setWatchedSeconds(0);
    setLastPlayedSeconds(0);
    setQuizStarted(false);
    setQuizAnswers({});
    setQuizSubmitted(false);
    setQuizScore(null);
  };

  const handleVideoProgress = ({ playedSeconds }) => {
    const delta = playedSeconds - lastPlayedSeconds;
    if (delta > 0 && delta < 5) {
      setWatchedSeconds(prev => prev + delta);
    }
    setLastPlayedSeconds(playedSeconds);
  };

  const handleVideoEnd = async () => {
    const isWatched = hasWatchedVideo(watchedSeconds, currentDocument.duration);
    if (isWatched) {
      await handleVideoComplete(currentDocument.documentId);
    }
  };

  const handleCloseVideo = async () => {
    const alreadyCompleted = data[currentDocument.documentId];
    const isTrackable = currentDocument.isTrackable;

    if (!isTrackable || alreadyCompleted || hasWatchedVideo(watchedSeconds, currentDocument.duration)) {
      if (!alreadyCompleted) {
        await handleVideoComplete(currentDocument.documentId);
      }
      setVideoOpen(false);
    } else {
      setShowEarlyCloseWarning(true);
    }
  };

  const handleStartQuiz = () => {
    setQuizStarted(true);
  };

  const handleQuizAnswer = (questionId, answer) => {
    setQuizAnswers((prev) => ({
      ...prev,
      [questionId]: answer,
    }));
  };

  const handleSubmitQuiz = () => {
    if (currentDocument && currentDocument.quiz) {
      const correctAnswers = currentDocument.quiz.questions.filter(
        (q) => quizAnswers[q.id] === q.correctAnswer
      ).length;
      const score = (correctAnswers / currentDocument.quiz.questions.length) * 100;
      setQuizScore(score);
      setQuizSubmitted(true);

      if (score >= 80) {
        handleVideoComplete(currentDocument.documentId);
      }
    }
  };


  const renderQuiz = () => {
    if (!currentDocument || !currentDocument.quiz) return null;

    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          Training Quiz
        </Typography>
        {currentDocument.quiz.questions.map((question) => (
          <Box key={question.id} sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              {question.question}
            </Typography>
            <List>
              {question.options.map((option, index) => (
                <ListItem
                  key={index}
                  button
                  selected={quizAnswers[question.id] === index}
                  onClick={() => handleQuizAnswer(question.id, index)}
                  disabled={quizSubmitted}
                >
                  <ListItemText primary={option} />
                  {quizSubmitted && (
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: index === question.correctAnswer ? 'success.main' : 'error.main',
                        fontWeight: 600,
                        fontSize: '12px'
                      }}
                    >
                      {index === question.correctAnswer ? '✓ Correct' : '✗ Incorrect'}
                    </Typography>
                  )}
                </ListItem>
              ))}
            </List>
          </Box>
        ))}
        {!quizSubmitted ? (
          <Button
            variant="contained"
            onClick={handleSubmitQuiz}
            disabled={Object.keys(quizAnswers).length !== currentDocument.quiz.questions.length}
          >
            Submit Quiz
          </Button>
        ) : (
          <Alert severity={quizScore >= 80 ? 'success' : 'error'} sx={{ mt: 2 }}>
            Your score: {quizScore}%
            {quizScore < 80 && ' - You need 80% to pass. Please try again.'}
          </Alert>
        )}
      </Box>
    );
  };

  if (!user) return <div>Loading...</div>;

  // Only show actual training documents, sorted by submodule
  const trainingModules = documents.length > 0 ? documents.map((doc, index) => ({
    id: doc.documentId || index,
    title: doc.documentName,
    description: doc.description || 'Training material',
    document: doc, // Store the actual document for reference
    subModule: doc.subModule || 'General',
    isTrackable: doc.isTrackable,
    pageCount: doc.pageCount,
    duration: doc.duration,
  })).sort((a, b) => a.subModule.localeCompare(b.subModule)) : [];

  // Group documents by submodule
  const groupedBySubModule = trainingModules.reduce((acc, module) => {
    if (!acc[module.subModule]) acc[module.subModule] = [];
    acc[module.subModule].push(module);
    return acc;
  }, {});

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>
      <Typography variant="h5" sx={{ fontWeight: 600, fontSize: '20px' }} gutterBottom>
        Training Modules
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph sx={{ fontSize: '16px', mb: 5 }}>
        Complete the following training modules to enhance your skills and knowledge.
      </Typography>

      {trainingModules.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body1" color="text.secondary">
            No training documents available at the moment.
          </Typography>
        </Box>
      ) : (
        Object.entries(groupedBySubModule).map(([subModule, modules]) => (
          <Box key={subModule} sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ 
              mb: 3, 
              fontSize: '22px', 
              fontWeight: 700,
              color: '#2c3e50',
              borderBottom: '2px solid #e8eaed',
              pb: 1.5,
              textAlign: 'center'
            }}>
              {subModule}
            </Typography>
            <Grid container spacing={3}>
              {modules.map((module) => (
                <Grid item xs={12} sm={6} key={module.id}>
                  <Card sx={{ 
                    p: 3, 
                    borderRadius: 3,
                    boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                    transition: 'all 0.3s ease',
                    border: '1px solid #f0f0f0',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 25px rgba(0,0,0,0.12)',
                      borderColor: '#e0e0e0'
                    }
                  }}>
                    <CardContent sx={{ p: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 3 }}>
                        {module.document.docType === 'video/mp4' ? (
                          <PlayIcon color="primary" sx={{ mr: 2, fontSize: '2.5rem', mt: 0.5, color: '#7e57c2' }} />
                        ) : (
                          <DescriptionIcon color="primary" sx={{ mr: 2, fontSize: '2.5rem', mt: 0.5, color: '#7e57c2' }} />
                        )}
                        <Box sx={{ flex: 1 }}>
                          <Typography 
                            variant="h5" 
                            sx={{ 
                              mb: 2, 
                              fontWeight: 700,
                              fontSize: '18px',
                              color: '#2c3e50',
                              lineHeight: 1.3
                            }}
                          >
                            {module.title}
                          </Typography>
                          {module.description && (
                            <Box sx={{
                              background: '#ffffff',
                              border: '1px solid #e8eaed',
                              borderRadius: 2,
                              p: 1.5,
                              position: 'relative',
                              width: '100%',
                              '&::before': {
                                content: '""',
                                position: 'absolute',
                                left: 0,
                                top: 0,
                                bottom: 0,
                                width: '4px',
                                background: '#7e57c2',
                                borderRadius: '0 2px 2px 0'
                              }
                            }}>
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  lineHeight: 1.4,
                                  fontSize: '16px',
                                  fontWeight: 400,
                                  color: '#5f6368',
                                  m: 0
                                }}
                              >
                                {module.description}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Box>


                      {module.document.docType === 'video/mp4' ? (
                        <>
                          <Typography 
                            variant="body1" 
                            sx={{ 
                              fontSize: '16px',
                              fontWeight: 600,
                              color: '#5f6368',
                              mb: 2.5
                            }}
                          >
                            Watch this training video and complete the quiz
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Button 
                              variant={data[module.document.documentId] ? "contained" : "outlined"} 
                              size="medium" 
                              onClick={() => handleOpenVideo(module.document)}
                              color="primary"
                              sx={{
                                fontWeight: 600,
                                fontSize: '14px',
                                px: 2,
                                py: 1,
                                borderRadius: 2,
                                textTransform: 'none',
                                backgroundColor: data[module.document.documentId] ? '#7e57c2' : 'transparent',
                                color: data[module.document.documentId] ? '#ffffff' : '#7e57c2',
                                borderColor: '#7e57c2',
                                '&:hover': {
                                  backgroundColor: data[module.document.documentId] ? '#6a4c93' : 'rgba(126, 87, 194, 0.08)',
                                  borderColor: '#6a4c93'
                                },
                                transition: 'all 0.2s ease'
                              }}
                            >
                              {data[module.document.documentId] ? 'Training Completed' : 'Start Training'}
                            </Button>
                          </Box>
                        </>
                      ) : (
                        <>
                          <Typography 
                            variant="body1" 
                            sx={{ 
                              fontSize: '16px',
                              fontWeight: 600,
                              color: '#5f6368',
                              mb: 2.5
                            }}
                          >
                            Please read and understand this training material
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Button
                              variant={data[module.document.documentId] ? "contained" : "outlined"} 
                              size="medium" 
                              onClick={() => handleOpenDocument(module.document)}
                              color="primary"
                              sx={{
                                fontWeight: 600,
                                fontSize: '14px',
                                px: 2,
                                py: 1,
                                borderRadius: 2,
                                textTransform: 'none',
                                backgroundColor: data[module.document.documentId] ? '#7e57c2' : 'transparent',
                                color: data[module.document.documentId] ? '#ffffff' : '#7e57c2',
                                borderColor: '#7e57c2',
                                '&:hover': {
                                  backgroundColor: data[module.document.documentId] ? '#6a4c93' : 'rgba(126, 87, 194, 0.08)',
                                  borderColor: '#6a4c93'
                                },
                                transition: 'all 0.2s ease'
                              }}
                            >
                              {data[module.document.documentId] ? 'Material Read' : 'View Material'}
                            </Button>
                          </Box>
                        </>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        ))
      )}

      {/* Video Dialog */}
      <Dialog open={videoOpen} onClose={handleCloseVideo} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontSize: '18px', fontWeight: 600 }}>
          {currentDocument?.documentName}
          <IconButton aria-label="close" onClick={handleCloseVideo} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {!quizStarted ? (
            <Box>
              <Box sx={{ position: 'relative', paddingTop: '56.25%' }}>
                <ReactPlayer
                  url={`${API_URL}/documents/stream/${currentDocument?.filePath}`}
                  width="100%"
                  height="100%"
                  style={{ position: 'absolute', top: 0, left: 0 }}
                  controls
                  onProgress={handleVideoProgress}
                  onEnded={handleVideoEnd}
                />
              </Box>
              <LinearProgress
                variant="determinate"
                value={(watchedSeconds / (currentDocument?.duration || 1)) * 100}
                sx={{ mt: 1, height: 4 }}
              />
              {watchedSeconds >= (currentDocument?.duration || 0) * 0.9 && (
                <Button
                  variant="contained"
                  onClick={handleStartQuiz}
                  sx={{ mt: 2 }}
                >
                  Start Quiz
                </Button>
              )}
            </Box>
          ) : (
            renderQuiz()
          )}
        </DialogContent>
      </Dialog>

      {/* Document Dialog */}
      <Dialog open={documentOpen} onClose={handleCloseDocument} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontSize: '18px', fontWeight: 600 }}>
          {currentDocument?.documentName}
          <IconButton
            aria-label="close"
            onClick={handleCloseDocument}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {currentDocument && (
            <>
              {/* Display images directly */}
              {currentDocument.docType && currentDocument.docType.startsWith('image/') ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <img
                    src={`${API_URL}/documents/stream/${currentDocument.filePath || currentDocument.fileName}`}
                    alt={currentDocument.documentName}
                    style={{
                      maxWidth: '100%',
                      maxHeight: '600px',
                      objectFit: 'contain',
                      border: '1px solid #e0e0e0',
                      borderRadius: '4px'
                    }}
                    onError={(e) => {
                      console.error('Error loading image:', e);
                      e.target.style.display = 'none';
                      // Show error message
                      const errorDiv = document.createElement('div');
                      errorDiv.innerHTML = `
                        <div style="text-align: center; padding: 20px; color: #666;">
                          <p>Failed to load image</p>
                          <p>${currentDocument.documentName}</p>
                        </div>
                      `;
                      e.target.parentNode.appendChild(errorDiv);
                    }}
                  />
                </Box>
              ) : (
                /* Display PDFs and other documents in iframe */
                <iframe
                  src={`${API_URL}/documents/stream/${currentDocument.filePath || currentDocument.fileName}`}
                  width="100%"
                  height="600px"
                  title={currentDocument.documentName}
                  style={{ border: 'none' }}
                  onError={(e) => {
                    console.error('Error loading document:', e);
                  }}
                />
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Early Close Warning Dialog */}
      <Dialog open={showEarlyCloseWarning}>
        <DialogTitle>Warning</DialogTitle>
        <DialogContent>
          <Typography>You haven't completed this training content. Are you sure you want to close?</Typography>
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button onClick={() => setShowEarlyCloseWarning(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={() => {
                setShowEarlyCloseWarning(false);
                setVideoOpen(false);
                setDocumentOpen(false);
              }}
            >
              Close Anyway
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default TrainingStep; 